import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  catchError,
  EMPTY,
  exhaustMap,
  from,
  map,
  Observable,
  Subject,
  takeUntil,
  tap,
} from "rxjs";
import { ApiService, Tables } from "src/app/services/api.service";
import {
  transformDatabaseObject,
  transformUserForDatabase,
  User,
  UserDB,
} from "src/app/shared/interfaces/user";

interface UserState {
  users: User[];
  userBeingEdited: User | null;
}

@Injectable({
  providedIn: "root",
})
export class UserDataService {
  apiService = inject(ApiService);

  // sources
  users$: Observable<UserDB[]> = this.apiService.getUsers();
  add$ = new Subject<User>();
  update$ = new Subject<User>();
  delete$ = new Subject<string>();
  userBeingEdited$ = new Subject<User>();

  // state
  private state = signal<UserState>({
    users: [],
    userBeingEdited: null,
  });

  // selectors
  users = computed(() => this.state().users);
  userBeingEdited = computed(() => this.state().userBeingEdited);

  constructor() {
    // reducers
    this.users$
      .pipe(
        takeUntilDestroyed(),
        map((users: UserDB[]) =>
          users.map((user) => transformDatabaseObject(user as UserDB))
        )
      )
      .subscribe((users: User[]) =>
        this.state.update((state) => ({
          ...state,
          users,
        }))
      );

    this.add$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((user) => this.addUser(user)),
        map((user) => transformDatabaseObject(user)),
        catchError((error) => {
          console.error("Error adding user", error);
          return EMPTY;
        })
      )
      .subscribe((newUser) => {
        this.state.update((state) => ({
          ...state,
          users: [...state.users, newUser],
        }));
      });

    this.update$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((user) => this.updateUser(user)),
        map((updatedUserDB) => transformDatabaseObject(updatedUserDB)),
        catchError((error) => {
          console.error("Error updating user", error);
          return EMPTY;
        })
      )
      .subscribe((updatedUser) => {
        this.state.update((state) => ({
          ...state,
          users: state.users.map((user) =>
            user.id === updatedUser.id ? updatedUser : user
          ),
        }));
      });

    this.delete$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((id) =>
          from(this.deleteUser(id)).pipe(
            tap(() => {
              this.state.update((state) => ({
                ...state,
                users: state.users.filter((user) => user.id !== id),
              }));
            }),
            catchError((error) => {
              console.error("Error deleting user:", error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();

    this.userBeingEdited$.pipe(takeUntilDestroyed()).subscribe((user) =>
      this.state.update((state) => ({
        ...state,
        userBeingEdited: user,
      }))
    );
  }

  private addUser(user: User): Observable<UserDB> {
    return this.apiService.createNewUser(transformUserForDatabase(user));
  }

  private updateUser(user: User): Observable<UserDB> {
    return this.apiService.updateUser(transformUserForDatabase(user));
  }

  private deleteUser(id: string) {
    return this.apiService.deleteItem(Tables.User, id);
  }
}
